/**
 * @fileoverview Canonical state types for the shared import run flow.
 * @module import-editor/runFlow/runFlowTypes
 *
 * Responsibilities:
 * - Define the single `ImportRunFlowState` the shared run flow owns, as the
 *   union of the state previously duplicated between tenant-web's
 *   ImportWizardContainer and system-web's SystemImportWizardContainer.
 * - Define the dry-run summary / row shapes rendered by the preview and
 *   commit steps.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for template / mapping / run types
 * - `../components/ImportProgressIndicator` for the progress phase vocabulary
 *
 * Architecture:
 * - Types only — no runtime logic, no React, no host imports.
 * - Host-specific fields (spatial filter, draft-run id) are always present
 *   on the state; host feature flags in `UnifiedImportRunFlow` decide
 *   whether the UI surfaces them and whether the hook persists them.
 *
 * Notes:
 * - `dryRunSummary` follows the April 2026 taxonomy (`excluded.*` /
 *   `failedByKind.*`) — NO legacy `skipped` / `failed` / `outOfBounds` fields.
 */

import type {
  ImportTemplate,
  ImportTemplateListItem,
  FieldMapping,
  ImportDataMode,
} from '@firesquirrel/local-db';
import type { ProgressPhase, ProgressStage, ProgressSummary } from '../components/ImportProgressIndicator';
import type { ParsedFilePreview } from '../unifiedWizard/services';

// ---------------------------------------------------------------------------
// Spatial filter (tenant-web GeoParquet sources)
// ---------------------------------------------------------------------------

/** Spatial filter mode for GeoParquet parcel extraction. */
export type SpatialFilterMode = 'all' | 'department' | 'fireHazard';

// ---------------------------------------------------------------------------
// Dry-run shapes
// ---------------------------------------------------------------------------

/**
 * @contract RunFlowDryRunRow
 *
 * Purpose:
 * - One row in the preview step's inline error table (rendered when the
 *   host does not implement the `/rows` row explorer).
 *
 * Fields:
 * - rowNumber: 1-based source row
 * - matchKeyValue: display value for the row's match key
 * - status: create/update/skip/error classification
 * - reason: human-readable explanation (errors carry the failure message)
 */
export interface RunFlowDryRunRow {
  rowNumber: number;
  matchKeyValue: string;
  status: 'create' | 'update' | 'skip' | 'error';
  reason?: string;
}

/**
 * @contract RunFlowDryRunSummary
 *
 * Purpose:
 * - Wizard-facing dry-run summary. April 2026 taxonomy only.
 *
 * Fields:
 * - totalRows/created/updated/softDeleted: top-level counters
 * - excluded/failedByKind: nested skip + failure sub-buckets
 * - previewRows: optional per-row preview with enrichment data (parcel mode)
 * - enrichmentCounts: optional aggregate enrichment counts over all rows
 */
export interface RunFlowDryRunSummary {
  totalRows: number;
  created: number;
  updated: number;
  softDeleted: number;
  excluded: {
    outOfJurisdiction: number;
    missingCoordinates: number;
    duplicateInsertOnly: number;
    missingMatchKey: number;
    /** parcelOwnerUpdate only: no entity matched (update-only). */
    noMatch?: number;
    /** parcelOwnerUpdate only: existing ownership.sourceAsOf is newer. */
    staleSource?: number;
  };
  failedByKind: {
    transform: number;
    entityBuild: number;
  };
  /** Per-row preview entries with enrichment data (owner parsing preview). */
  previewRows?: Array<{
    rowNumber: number;
    matchKeyValue: string;
    status: string;
    reason?: string;
    enrichment?: {
      needsEnrichment: boolean;
      enrichmentType: 'ai' | 'none';
      missingFields: string[];
      pathway?: 'ai' | 'none' | 'skip';
      parsedOwner?: { rawInput: string; parsed: Record<string, string> };
    };
  }>;
  /** Authoritative aggregate enrichment counts over ALL evaluated rows. */
  enrichmentCounts?: {
    evaluated: number;
    needsEnrichment: number;
    complete: number;
    ai: number;
    noProvider: number;
  };
}

// ---------------------------------------------------------------------------
// Wizard state
// ---------------------------------------------------------------------------

/**
 * @contract ImportRunFlowState
 *
 * Purpose:
 * - The full run-flow wizard state: upload → parse → map → match-key →
 *   dry-run → commit.
 *
 * Rules:
 * - Components never call services directly; the `useImportRunFlow` hook
 *   owns all transitions.
 *
 * Sync:
 * - Run state is persisted through the host's adapter (draft-run patch or
 *   preview-session save) and projected back into Dexie by host services.
 */
export interface ImportRunFlowState {
  // ----- Metadata -------------------------------------------------------
  importName: string;
  importDescription: string;
  /** Null until the mode is known: template selection, geo-file
      auto-detection, or the manual picker (when enabled) all set it. */
  dataMode: ImportDataMode | null;

  // ----- Template selection --------------------------------------------
  selectedTemplate: ImportTemplateListItem | null;
  /** Template default for the jurisdiction boundary filter, captured when
      the template loads so the UI can show "(overriding template default)". */
  templateBoundaryCheckDefault: boolean;
  /** Effective boundary check for THIS run (per-run override). */
  boundaryCheckEnabled: boolean;

  // ----- Spatial filter (GeoParquet sources; tenant-web surfaces it) ----
  filterMode: SpatialFilterMode;
  /** Hazard severity levels for fireHazard filtering. Undefined/empty = all. */
  hazardLevels: string[] | undefined;

  // ----- File + parse ---------------------------------------------------
  file: File | null;
  /** Post-upload filename (may differ from `file.name` after conversion). */
  filename: string;
  fileSize: number | null;
  /** Original detected format before any backend conversion. */
  sourceFormat: string | null;
  uploadResult: { runId: string; blobUrl: string | null; fileType?: string } | null;
  parseResult: ParsedFilePreview | null;
  selectedSheet: string | null;

  // ----- Mapping + behaviour -------------------------------------------
  fieldMappings: FieldMapping[];
  importBehavior: ImportTemplate['importBehavior'];
  /**
   * Phase 5a (`dataMode === 'parcelOwnerUpdate'` only): ISO date input
   * collected on the upload step. Required before the run can be created —
   * it becomes `run.dataAsOf`, the freshness claim stamped to
   * `ownership.sourceAsOf` and compared by the atomic update guard.
   */
  dataAsOf: string;

  // ----- Draft run (system-web persistence) ------------------------------
  /** Local-first draft run id. Null on hosts without draft-run persistence. */
  runId: string | null;

  // ----- Dry run --------------------------------------------------------
  dryRunId: string | null;
  dryRunSummary: RunFlowDryRunSummary | null;
  /** Error-only rows for the inline table (hosts without the row explorer). */
  dryRunRows: RunFlowDryRunRow[];
  proceedWithErrors: boolean;

  // ----- Commit ---------------------------------------------------------
  commitRunId: string | null;
  commitStatus: 'idle' | 'committing' | 'completed' | 'failed';
  commitError: string | null;
}

export const EMPTY_RUN_FLOW_STATE: ImportRunFlowState = {
  importName: '',
  importDescription: '',
  dataMode: null,
  selectedTemplate: null,
  templateBoundaryCheckDefault: false,
  boundaryCheckEnabled: false,
  filterMode: 'all',
  hazardLevels: ['extreme'],
  file: null,
  filename: '',
  fileSize: null,
  sourceFormat: null,
  uploadResult: null,
  parseResult: null,
  selectedSheet: null,
  fieldMappings: [],
  importBehavior: 'upsert',
  dataAsOf: '',
  runId: null,
  dryRunId: null,
  dryRunSummary: null,
  dryRunRows: [],
  proceedWithErrors: false,
  commitRunId: null,
  commitStatus: 'idle',
  commitError: null,
};

// ---------------------------------------------------------------------------
// Live progress snapshot
// ---------------------------------------------------------------------------

/**
 * @contract RunFlowLiveProgress
 *
 * Purpose:
 * - Live progress snapshot fed by the run poller's onProgress callback and
 *   consumed by ImportProgressIndicator on the dry-run and commit steps.
 */
export interface RunFlowLiveProgress {
  summary: ProgressSummary | null;
  phase: ProgressPhase;
  startedAt?: string;
  errorMessage?: string | null;
  stage?: ProgressStage | null;
  stageDetail?: string | null;
}

export const RUN_FLOW_LIVE_PROGRESS_EMPTY: RunFlowLiveProgress = {
  summary: null,
  phase: 'queued',
};
